// Widget-level operations for the dashboard container: save
// (insert-or-replace with first-fit placement), duplicate, remove, and the
// layout-edit draft (seeded on entry, committed as ONE config PATCH on Save,
// discarded on Cancel). Kept out of `Dashboard.tsx` so the route container
// stays within the component line budget and this logic stays hook-testable.

import { useCallback, useMemo, useState } from "react";

import { placeNew, type LayoutItem } from "./dashboardLayout";
import { newWidgetId, type DashboardWidget } from "./dashboardWidgets";
import type { DashboardBoardsApi } from "./useDashboardBoards";

export interface UseDashboardWidgetActionsOptions {
  boards: DashboardBoardsApi;
  dashboardId: string | undefined;
  isLayoutEditing: boolean;
  widgets: DashboardWidget[];
  onWidgetSaved: () => void;
  onLayoutSaved: () => void;
}

export interface DashboardWidgetActions {
  gridItems: LayoutItem[];
  onDuplicateWidget: (widget: DashboardWidget) => void;
  onLayoutChange: (items: LayoutItem[]) => void;
  onRemoveWidget: (widget: DashboardWidget) => void;
  onSaveWidget: (widget: DashboardWidget) => void;
  saveLayout: () => void;
}

interface LayoutDraft {
  dashboardId: string | undefined;
  items: LayoutItem[];
}

export function useDashboardWidgetActions({
  boards,
  dashboardId,
  isLayoutEditing,
  widgets,
  onLayoutSaved,
  onWidgetSaved,
}: UseDashboardWidgetActionsOptions): DashboardWidgetActions {
  // Layout-edit draft: seeded from the saved config on entry, committed as
  // one PATCH on Save, discarded on Cancel (explicit edit mode).
  const [layoutDraft, setLayoutDraft] = useState<LayoutDraft | null>(null);
  if (!isLayoutEditing && layoutDraft !== null) {
    setLayoutDraft(null);
  } else if (
    isLayoutEditing &&
    (layoutDraft === null || layoutDraft.dashboardId !== dashboardId)
  ) {
    setLayoutDraft({
      dashboardId,
      items: widgets.map((widget) => ({
        id: widget.id,
        layout: widget.layout,
      })),
    });
  }

  const saveWidgets = useCallback(
    (nextWidgets: readonly DashboardWidget[], onDone?: () => void) => {
      boards.patchConfig.mutate(nextWidgets, {
        onSuccess: () => {
          onDone?.();
        },
      });
    },
    [boards.patchConfig],
  );

  const onSaveWidget = useCallback(
    (widget: DashboardWidget) => {
      const existing = widgets.find((candidate) => candidate.id === widget.id);
      let nextWidgets: DashboardWidget[];
      if (existing) {
        nextWidgets = widgets.map((candidate) =>
          candidate.id === widget.id ? widget : candidate,
        );
      } else {
        const items = widgets.map((candidate) => ({
          id: candidate.id,
          layout: candidate.layout,
        }));
        const placed = placeNew(
          items,
          widget.layout.width,
          widget.layout.height,
        );
        nextWidgets = [...widgets, { ...widget, layout: placed }];
      }
      saveWidgets(nextWidgets, onWidgetSaved);
    },
    [onWidgetSaved, saveWidgets, widgets],
  );

  const onDuplicateWidget = useCallback(
    (widget: DashboardWidget) => {
      const items = widgets.map((candidate) => ({
        id: candidate.id,
        layout: candidate.layout,
      }));
      const placed = placeNew(items, widget.layout.width, widget.layout.height);
      saveWidgets([
        ...widgets,
        {
          ...widget,
          id: newWidgetId(),
          title: `${widget.title} copy`,
          layout: placed,
        },
      ]);
    },
    [saveWidgets, widgets],
  );

  const onRemoveWidget = useCallback(
    (widget: DashboardWidget) => {
      saveWidgets(widgets.filter((candidate) => candidate.id !== widget.id));
    },
    [saveWidgets, widgets],
  );

  const onLayoutChange = useCallback(
    (items: LayoutItem[]) => {
      setLayoutDraft({ dashboardId, items });
    },
    [dashboardId],
  );

  const saveLayout = useCallback(() => {
    if (!layoutDraft) {
      onLayoutSaved();
      return;
    }
    const byId = new Map(
      layoutDraft.items.map((item) => [item.id, item.layout]),
    );
    saveWidgets(
      widgets.map((widget) => ({
        ...widget,
        layout: byId.get(widget.id) ?? widget.layout,
      })),
      onLayoutSaved,
    );
  }, [layoutDraft, onLayoutSaved, saveWidgets, widgets]);

  const gridItems = useMemo(
    () =>
      isLayoutEditing && layoutDraft
        ? layoutDraft.items
        : widgets.map((widget) => ({ id: widget.id, layout: widget.layout })),
    [isLayoutEditing, layoutDraft, widgets],
  );

  return {
    gridItems,
    onDuplicateWidget,
    onLayoutChange,
    onRemoveWidget,
    onSaveWidget,
    saveLayout,
  };
}
